import { forwardRef, memo } from 'react';
import classNames from 'classnames';
import { GridLegacy as Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import i18n from '@shared/config/i18n';
import {
  FormControl,
  BaseInput,
  BaseInputLabel,
  BaseInputHelperText,
  BaseInputErrorText,
  Divider,
} from '../..';

const { t } = i18n;

const useStyles = makeStyles({
  item: {},
  fullWidth: {
    width: '100%',
  },
  labelStart: {
    marginRight: 20,
  },
  labelEnd: {
    marginLeft: 20,
  },
  labelTop: {
    marginBottom: 10,
  },
  labelBottom: {
    marginTop: 10,
  },
  inputContainer: { display: 'flex', alignItems: 'center' },
});

const mapDirectionToLabelPlacement = {
  bottom: 'column-reverse',
  end: 'row-reverse',
  start: 'row',
  top: 'column',
};

const defaultPlaceholder = t('FillField');

const InputControl = memo(
  forwardRef((props, ref) => {
    const {
      divider = true,
      fullWidth = true,
      labelPlacement: lp = 'top',
      name,
      placeholder = defaultPlaceholder,
      helpText = '',
      label = 'Some label',
      errors,
      type = 'text',
      rowsMax = 1,
      multiline = false,
      inputProps,
      inputRef,
      inputButton,
      ...rest
    } = props;

    const classes = useStyles();

    const getName = (string) =>
      typeof string === 'string' || Math.random().toString(36).slice(2, 11);

    const inputId = `${getName(name)}-input`;
    const { item, fullWidth: fw, inputContainer } = classes;

    const direction = mapDirectionToLabelPlacement[lp];
    const rootClassName = classNames(item, { [fw]: fullWidth });

    const hasHelpText = Boolean(helpText);

    const hasInputButton = Boolean(inputButton) && props.mode === 'edit';

    return (
      <FormControl {...rest}>
        <Grid
          container
          direction={direction}
          alignItems="baseline"
          justifyContent="space-between"
          wrap="nowrap"
        >
          <InputLabel
            lp={lp}
            rootClassName={rootClassName}
            label={label}
            inputId={inputId}
          />

          <Grid item className={rootClassName}>
            <Grid container direction="column">
              <Grid item className={inputContainer}>
                <BaseInput
                  fullWidth={!!fullWidth}
                  placeholder={placeholder}
                  name={name}
                  id={inputId}
                  type={type}
                  multiline={multiline}
                  maxRows={rowsMax}
                  inputProps={inputProps}
                  inputRef={inputRef}
                  {...rest}
                  ref={ref}
                />
                {hasInputButton ? inputButton : null}
              </Grid>
              <Grid item>
                <BaseInputHelperText>{hasHelpText ? helpText : null}</BaseInputHelperText>
              </Grid>

              <Grid item>
                <BaseInputErrorText errors={errors} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider isVisible={divider} />
      </FormControl>
    );
  }),
  (prev, next) =>
    prev.value === next.value &&
    prev.label === next.label &&
    prev.mode === next.mode &&
    prev.required === next.required &&
    prev.readOnly === next.readOnly &&
    prev.error === next.error &&
    prev.disabled === next.disabled &&
    JSON.stringify(prev.errors) === JSON.stringify(next.errors) &&
    prev.helpText === next.helpText &&
    prev.state === next.state
);

function InputLabel(props) {
  const { inputId, label, lp, rootClassName } = props;

  const { labelStart, labelEnd, labelTop, labelBottom } = useStyles();

  const labelClassName = classNames({
    [labelStart]: lp === 'start',
    [labelEnd]: lp === 'end',
    [labelTop]: lp === 'top',
    [labelBottom]: lp === 'bottom',
  });

  const onClick = (e) => {
    e.preventDefault();
  };

  if (!label) {
    return null;
  }

  return (
    <Grid item className={rootClassName} style={{ width: 'auto' }}>
      <BaseInputLabel
        classes={{
          root: labelClassName,
        }}
        htmlFor={inputId}
        onClick={onClick}
      >
        {label}
      </BaseInputLabel>
    </Grid>
  );
}

export { InputControl };
